import { useEffect, useMemo, useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { SlidersHorizontal, X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { MonthPickerInput } from '@/shared/ui/month-picker-input'
import { t } from '@/shared/lib/i18n'
import { clearOrgFiltersParams } from '@/shared/lib/persistedOrgFilters'
import { reportsMonthlyPath } from '@/shared/lib/routes'
import { isReportRangeOrderInvalid } from '@/shared/lib/report-range-order'
import {
  clearMonthlyReportFilters,
  readMonthlyReportFilters,
  writeMonthlyReportFilters,
} from '@/shared/lib/monthlyReportFiltersSession'
import type { SharedProps } from '@/shared/types/SharedProps'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { ReportRangeOrderError } from './ReportRangeOrderError'

interface SelectOption {
  value: number
  label: string
  siteId?: number | null
}

interface ReportFiltersShape {
  siteId?: string
  departmentId?: string
  monthFrom?: string
  monthTo?: string
  showAll?: string
  myQueue?: string
}

interface Props {
  filters: ReportFiltersShape
  siteOptions: SelectOption[]
  departmentOptions: SelectOption[]
  canShowAll: boolean
  triggerOnly?: boolean
  desktopOnly?: boolean
}

const ALL_SENTINEL = 'all'

const selectedFilterValue = (value: string | undefined): string => value || ALL_SENTINEL

const ReportFilters = ({
  filters,
  siteOptions,
  departmentOptions,
  canShowAll,
  triggerOnly = false,
  desktopOnly = false,
}: Props) => {
  const { currentTenantId, currentUserInfo } = usePage<SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null
  const [siteId, setSiteId] = useState(selectedFilterValue(filters.siteId))
  const [departmentId, setDepartmentId] = useState(selectedFilterValue(filters.departmentId))
  const [monthFrom, setMonthFrom] = useState(filters.monthFrom ?? '')
  const [monthTo, setMonthTo] = useState(filters.monthTo ?? '')
  const [showAll, setShowAll] = useState(
    filters.showAll === '1' || (canShowAll && filters.showAll !== '0'),
  )
  const [myQueue, setMyQueue] = useState(filters.myQueue !== '0')

  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()
  const hydrateRanRef = useRef<string | null>(null)

  useEffect(() => {
    setSiteId(selectedFilterValue(filters.siteId))
    setDepartmentId(selectedFilterValue(filters.departmentId))
    setMonthFrom(filters.monthFrom ?? '')
    setMonthTo(filters.monthTo ?? '')
    setShowAll(filters.showAll === '1' || (canShowAll && filters.showAll !== '0'))
    setMyQueue(filters.myQueue !== '0')
  }, [
    filters.siteId,
    filters.departmentId,
    filters.monthFrom,
    filters.monthTo,
    filters.showAll,
    filters.myQueue,
    canShowAll,
  ])

  const filteredDepartmentOptions = useMemo(() => {
    if (siteId === ALL_SENTINEL) return departmentOptions
    return departmentOptions.filter((dept) => dept.siteId == null || String(dept.siteId) === siteId)
  }, [siteId, departmentOptions])

  const navigate = (
    overrides: Record<string, string | undefined> = {},
    mode: FilterVisitMode = 'default',
  ) => {
    const s = overrides.siteId ?? siteId
    const d = overrides.departmentId ?? departmentId
    const mf = overrides.monthFrom ?? monthFrom
    const mt = overrides.monthTo ?? monthTo
    const sa = overrides.showAll ?? (showAll ? '1' : '0')
    const mq = overrides.myQueue ?? (myQueue ? '1' : '0')

    if (isReportRangeOrderInvalid(mf, mt)) return

    const params: Record<string, string> = {}
    // Always send org filter keys (empty = All) so the server can write session persistence
    // and distinguish apply/deselect from bare navigation.
    params.site_id = s === ALL_SENTINEL ? '' : s
    params.department_id = d === ALL_SENTINEL ? '' : d
    if (mf) params.month_from = mf
    if (mt) params.month_to = mt
    if (canShowAll) params.show_all = sa
    params.my_queue = mq

    // Write to the isolated monthly bucket (does not affect the shared multiselect store).
    writeMonthlyReportFilters(currentTenantId, currentUserId, {
      siteId: s !== ALL_SENTINEL ? Number(s) : null,
      departmentId: d !== ALL_SENTINEL ? Number(d) : null,
    })

    const visit = beginVisit(
      JSON.stringify(params),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
    if (!visit) return
    router.get(
      reportsMonthlyPath(params),
      {},
      { preserveState: visit.keepDropdownOpen, preserveScroll: visit.keepDropdownOpen },
    )
  }

  // Always restore stored site/dept when opening monthly reports (URL wins if present).
  // Bespoke effect — not the generic hook — because monthly is isolated from the
  // shared multiselect store and must not read from or write to it.
  useEffect(() => {
    // Index mounts two ReportFilters (mobile trigger + desktop). Hydrate once.
    if (triggerOnly) return
    if (currentTenantId == null) return
    const scopeKey = `${currentTenantId}:${currentUserId ?? 'unknown'}`
    if (hydrateRanRef.current === scopeKey) return
    hydrateRanRef.current = scopeKey

    const urlHasSite = Boolean(filters.siteId && filters.siteId !== ALL_SENTINEL)
    const urlHasDept = Boolean(filters.departmentId && filters.departmentId !== ALL_SENTINEL)

    if (urlHasSite || urlHasDept) {
      // URL carries org filters → write to isolated store (URL wins).
      writeMonthlyReportFilters(currentTenantId, currentUserId, {
        siteId: urlHasSite ? Number(filters.siteId) : null,
        departmentId: urlHasDept ? Number(filters.departmentId) : null,
      })
      return
    }

    // No URL org filters → read isolated store, prune against options, apply if non-empty.
    const stored = readMonthlyReportFilters(currentTenantId, currentUserId)
    if (stored.siteId == null) return

    const siteExists = siteOptions.some((option) => option.value === stored.siteId)
    if (!siteExists) return

    const deptExists =
      stored.departmentId != null &&
      departmentOptions.some(
        (option) =>
          option.value === stored.departmentId &&
          (option.siteId == null || option.siteId === stored.siteId),
      )

    const s = String(stored.siteId)
    const d = deptExists ? String(stored.departmentId) : ALL_SENTINEL
    setSiteId(s)
    setDepartmentId(d)
    navigate({ siteId: s, departmentId: d })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId, currentUserId, triggerOnly])

  const handleSiteChange = (val: string) => {
    setSiteId(val)
    setDepartmentId(ALL_SENTINEL)
    navigate({ siteId: val, departmentId: ALL_SENTINEL })
  }

  const handleDepartmentChange = (val: string) => {
    setDepartmentId(val)
    navigate({ departmentId: val })
  }

  const handleMonthFromChange = (val: string) => {
    setMonthFrom(val)
    navigate({ monthFrom: val })
  }

  const handleMonthToChange = (val: string) => {
    setMonthTo(val)
    navigate({ monthTo: val })
  }

  const handleShowAllChange = (val: boolean) => {
    setShowAll(val)
    navigate({ showAll: val ? '1' : '0' })
  }

  const handleMyQueueChange = (val: boolean) => {
    setMyQueue(val)
    navigate({ myQueue: val ? '1' : '0' })
  }

  const monthRangeInvalid = isReportRangeOrderInvalid(monthFrom, monthTo)

  const clearFilters = () => {
    setSiteId(ALL_SENTINEL)
    setDepartmentId(ALL_SENTINEL)
    setMonthFrom('')
    setMonthTo('')
    setShowAll(!!canShowAll)
    setMyQueue(true)
    clearMonthlyReportFilters(currentTenantId, currentUserId)
    router.get(reportsMonthlyPath(clearOrgFiltersParams()), {}, { preserveState: false })
  }

  const activeFilterCount = [
    siteId !== ALL_SENTINEL,
    departmentId !== ALL_SENTINEL,
    monthFrom,
    monthTo,
    canShowAll && !showAll,
    !myQueue,
  ].filter(Boolean).length

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.monthly.filterSite')}</Label>
        <SearchableSelect
          className="w-full lg:w-44"
          value={siteId}
          onValueChange={handleSiteChange}
          placeholder={t('reports.monthly.allSites')}
          options={[
            { value: ALL_SENTINEL, label: t('reports.monthly.allSites') },
            ...siteOptions.map((site) => ({ value: String(site.value), label: site.label })),
          ]}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.monthly.filterDepartment')}
        </Label>
        <SearchableSelect
          className="w-full lg:w-44"
          value={departmentId}
          onValueChange={handleDepartmentChange}
          placeholder={t('reports.monthly.allDepartments')}
          options={[
            { value: ALL_SENTINEL, label: t('reports.monthly.allDepartments') },
            ...filteredDepartmentOptions.map((dept) => ({
              value: String(dept.value),
              label: dept.label,
            })),
          ]}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.monthly.filterMonthFrom')}
        </Label>
        <MonthPickerInput
          value={monthFrom}
          onChange={handleMonthFromChange}
          className="w-full lg:w-40"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.monthly.filterMonthTo')}
        </Label>
        <MonthPickerInput
          value={monthTo}
          onChange={handleMonthToChange}
          className="w-full lg:w-40"
        />
      </div>

      <ReportRangeOrderError show={monthRangeInvalid} />

      {canShowAll && (
        <div className="flex flex-wrap items-center gap-2 lg:self-center">
          <Switch id="show-all" checked={showAll} onCheckedChange={handleShowAllChange} />
          <Label htmlFor="show-all" className="text-sm cursor-pointer">
            {t('reports.monthly.showAll')}
          </Label>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 lg:self-center">
        <Switch id="my-queue" checked={myQueue} onCheckedChange={handleMyQueueChange} />
        <Label htmlFor="my-queue" className="text-sm cursor-pointer">
          {t('reports.monthly.myQueue')}
        </Label>
      </div>
    </div>
  )

  return (
    <>
      {!desktopOnly && (
        <Sheet
          onOpenChange={(open) => {
            sheetOpenRef.current = open
            if (!open) navigate({}, 'sheet-close')
          }}
        >
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              {t('reports.monthly.filtersTitle')}
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="rounded-t-xl px-4 pt-4 pb-8"
            title={t('reports.monthly.filtersTitle')}
            hideCloseButton
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-semibold">{t('reports.monthly.filtersTitle')}</h2>
              <div className="flex flex-wrap items-center gap-2">
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={clearFilters}
                  >
                    {t('reports.monthly.filtersClear')}
                  </Button>
                )}
                <SheetClose asChild>
                  <Button variant="ghost" size="icon" aria-label={t('reports.monthly.filtersDone')}>
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
              </div>
            </div>
            <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
          </SheetContent>
        </Sheet>
      )}

      {!triggerOnly && (
        <div className="hidden rounded-md border p-4 lg:block">
          <div className="flex flex-col gap-3">
            {filterFields}
            {activeFilterCount > 0 && (
              <div className="flex items-center lg:justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={clearFilters}
                >
                  {t('reports.monthly.filtersClear')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default ReportFilters
